<template>
  <div className="component">
    <template v-show="isInitialized">
      <v-text-field
        v-model="newValue['formatted_address']"
        v-bind:disabled="autoDetermining"
        v-bind:loading="autoDetermining"
        v-bind:id="`${id}`"
        v-bind:label="label"
        v-bind:placeholder="autoSetInitialAddress || placeholder"
        v-on:input="onInputChanged"
        v-bind:autocomplete="new Date().getTime()"
      ></v-text-field>
    </template>
  </div>
</template>

<script type="text/babel">
import { mapGetters } from "vuex";
import { VTextField } from "vuetify/lib";
import GoogleMapsMixin from "@/mixins/googleMaps";
import _get from "lodash/get";
import _isString from "lodash/isString";

export default {
  name: "components-forms-address-index",
  watch: {
    value(newValue, oldValue) {
      if (!this.isDirty) {
        if (
          _get(newValue, "formatted_address") !==
          _get(oldValue, "formatted_address")
        ) {
          this.autoSet(true);
        }
      } else {
        this.onChange();
      }
    },
    isInitialized() {
      this.attachAutoComplete();
    }
  },
  props: {
    value: undefined,
    id: undefined,
    label: {
      default: undefined
    },
    placeholder: {
      default: undefined
    },
    returnKey: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      autoDetermining: false,
      autoSetInitialAddress: undefined,
      isDirty: false,
      autocompleteInstance: undefined,
      newValue: undefined,
      place: undefined
    };
  },
  methods: {
    getFormattedValue() {
      let formatted = {
        formatted_address: ""
      };

      if (this.value) {
        if (_isString(this.value)) {
          formatted = {
            formatted_address: this.value
          };
        } else {
          formatted = this.value;
        }
      }

      return formatted;
    },
    attachAutoComplete() {
      if (!this.isInitialized) {
        return;
      }

      if (this.autocompleteInstance) {
        return;
      }

      let autoCompleteInput = this.$el.querySelector("input");
      autoCompleteInput.placeholder = this.placeholder || "";

      this.autocompleteInstance = new google.maps.places.Autocomplete(
        autoCompleteInput,
        {
          componentRestrictions: {
            country: "us"
          }
        }
      );
      this.autocompleteInstance.place_changed = () => {
        this.place = this.autocompleteInstance.getPlace();
        this.onChange();
      };

      setTimeout(() => {
        /*
                    The google maps autocomplete instance overwrites the original input field
                    This causes autocomplete to be set to false, which as of this time of writing is broken
                    Since the instance itself doesn't fire an event when it makes this replacement we have to
                    add a timeout to update the autocomplete value again with a delay
                     */
        this.$el.querySelector("input").autocomplete = new Date().getTime();
        this.$el.querySelector("input").id = this.id || new Date().getTime();
      }, 500);

      // do not fire update on mount, otherwise the validation will "think" that the value was changed by the user and will delete any error messages
      this.autoSet(false);
    },
    onInputChanged() {
      this.place = null;
      this.isDirty = true;

      if (!this.place) {
        this.$emit("input", undefined);
      }
    },
    onChange() {
      const emitPayload = this.returnKey
        ? _get(this.addressComponents, this.returnKey)
        : this.addressComponents;
      this.$emit("input", emitPayload);
    },
    autoSet(fireOnChange) {
      let promise = undefined;

      if (this.newValue.formatted_address) {
        // Emit blank input so validation can't consider this finished until place determination finished
        this.autoSetInitialAddress = this.newValue.formatted_address;
        //this.$emit("input", undefined);

        this.autoDetermining = true;
        let addressParts = this.newValue.formatted_address.split(", ");

        while (addressParts.length > 0) {
          let address = addressParts.join(", ");
          addressParts.pop();

          if (!promise) {
            promise = this.getPlaceFromAddress(address);
          }

          promise = promise.then(place => {
            if (this.place) {
              return undefined;
            } else if (!place) {
              return this.getPlaceFromAddress(address);
            } else {
              this.place = place;
              this.newValue.formatted_address = this.place.formatted_address;
              if (fireOnChange) {
                this.onChange();
              }
            }
          });
        }

        // Handler for the last promise
        promise = promise.then(place => {
          if (!this.place && place) {
            this.place = place;
            this.newValue.formatted_address = this.place.formatted_address;
            if (fireOnChange) {
              this.onChange();
            }
          }
        });

        promise.finally(() => {
          this.autoSetInitialAddress = undefined;
          this.autoDetermining = false;
        });
      }
    },
    getPlaceFromAddress(address) {
      return new Promise((resolve, reject) => {
        this.googleMapsPlacePredictions(address)
          .then(place => {
            resolve(place);
          })
          .catch(e => {
            resolve();
          });
      });
    }
  },
  computed: {
    addressComponents() {
      let fields = [
        "street_address",
        "route",
        "intersection",
        "political",
        "country",
        "administrative_area_level_1",
        "administrative_area_level_2",
        "administrative_area_level_3",
        "administrative_area_level_4",
        "administrative_area_level_5",
        "colloquial_area",
        "locality",
        "sublocality",
        "neighborhood",
        "premise",
        "subpremise",
        "postal_code",
        "natural_feature",
        "airport",
        "park",
        "point_of_interest"
      ];

      if (this.place && this.place.formatted_address) {
        let values = {};
        let components = _get(this.place, "address_components", []);
        components.forEach(component => {
          fields.forEach(field => {
            let componentIndex = component.types.indexOf(field);

            if (componentIndex > -1) {
              values[`${field}_short_name`] = component["short_name"];
              values[`${field}_long_name`] = component["long_name"];
            }
          });
        });
        values.formatted_address = this.place.formatted_address;
        values.latitude = this.place.geometry ? this.place.geometry.location.lat() : undefined;
        values.longitude = this.place.geometry ? this.place.geometry.location.lng() : undefined;

        return values;
      }

      return undefined;
    },
    ...mapGetters("googleMaps", ["isInitialized"])
  },
  created() {
    this.newValue = this.getFormattedValue();
  },
  mounted() {
    this.attachAutoComplete();
  },
  beforeDestroy() {},
  mixins: [GoogleMapsMixin],
  components: {
    VTextField
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
</style>
